import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchMentorById, updateMentor } from '../../services/mentorService';
import { useUniversities } from '../../hooks/useUniversities';
import { generateGraduationYears, industryCategories, findIndustryCategory, OTHER_UNIVERSITY_ID, OTHER_UNIVERSITY_LABEL, OTHER_UNIVERSITY_NAME_MAX_LENGTH } from '../../data/mentorFormOptions';
import Layout from '../layout/Layout';
import './MentorProfile.css';
import '../students/StudentProfileEdit.css';
import '../auth/Auth.css';

const MentorProfileEdit = () => {

  const [mentor, setMentor] = useState(null);
  const [fullName, setFullName] = useState('');
  const [universityId, setUniversityId] = useState('');
  const [otherUniversityName, setOtherUniversityName] = useState('');
  const [graduationYear, setGraduationYear] = useState('');
  const [industryCategory, setIndustryCategory] = useState('');
  const [industrySubcategory, setIndustrySubcategory] = useState('');
  const [company, setCompany] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [messageToStudents, setMessageToStudents] = useState('');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { universities, loading: universitiesLoading } = useUniversities();

  useEffect(() => {
    const getMentorProfile = async () => {
      if (!currentUser) return;
      
      try {
        const mentorData = await fetchMentorById(currentUser.uid);
        setMentor(mentorData);
        
        // フォームに現在の値を設定
        setFullName(mentorData.氏名 || '');
        setGraduationYear(mentorData.卒業した年?.toString() || '');
        setCompany(mentorData.会社名 || '');
        setMessageToStudents(mentorData.一言メッセージ || '');
        
        // 業界の設定
        if (mentorData.就職先業界) {
          // 既存の業界データから分解
          const industryData = mentorData.就職先業界;
          const { foundCategory, foundSubcategory } = findIndustryCategory(industryData);
          
          setIndustryCategory(foundCategory);
          setIndustrySubcategory(foundSubcategory);
        }
        
        // プロフィール画像の設定
        if (mentorData.アイコンURL) {
          setImagePreview(mentorData.アイコンURL);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching mentor profile:', err);
        setError('OG情報の取得に失敗しました。');
        setLoading(false);
      }
    };

    getMentorProfile();
  }, [currentUser]);

  useEffect(() => {
    if (!mentor || universitiesLoading) return;

    if (mentor.大学ID === OTHER_UNIVERSITY_ID) {
      setUniversityId(OTHER_UNIVERSITY_ID);
      setOtherUniversityName(mentor.出身大学 || '');
      return;
    }

    if (mentor.大学ID && universities.some((uni) => uni.id === mentor.大学ID)) {
      setUniversityId(mentor.大学ID);
      setOtherUniversityName('');
      return;
    }

    const matchedUniversity = universities.find((uni) => uni.name === mentor.出身大学);
    if (matchedUniversity) {
      setUniversityId(matchedUniversity.id);
      setOtherUniversityName('');
      return;
    }

    if (mentor.出身大学) {
      setUniversityId(OTHER_UNIVERSITY_ID);
      setOtherUniversityName(mentor.出身大学);
    }
  }, [mentor, universities, universitiesLoading]);

  const handleUniversityChange = (e) => {
    const value = e.target.value;
    setUniversityId(value);
    if (value !== OTHER_UNIVERSITY_ID) {
      setOtherUniversityName('');
    }
  };

  // 画像変更処理
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // ファイルタイプチェック
    if (!file.type.startsWith('image/')) {
      setFieldErrors(prev => ({
        ...prev,
        profileImage: "画像ファイルを選択してください"
      }));
      e.target.value = null;
      return;
    }

    // ファイルサイズチェック (5MB制限)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setFieldErrors(prev => ({
        ...prev,
        profileImage: "画像サイズは5MB以下にしてください"
      }));
      e.target.value = null;
      return;
    }

    // 画像リサイズとプレビュー
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      const maxWidth = 400;
      const maxHeight = 400;
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob((blob) => {
        const reader = new FileReader();
        reader.onload = () => {
          const resizedDataUrl = reader.result;
          setProfileImage(resizedDataUrl);
          setImagePreview(resizedDataUrl);
          
          // エラークリア
          setFieldErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors.profileImage;
            return newErrors;
          });
        };
        reader.readAsDataURL(blob);
      }, 'image/jpeg', 0.9);
    };
    
    img.src = URL.createObjectURL(file);
  };

  // 業界カテゴリ変更時の処理
  const handleIndustryCategoryChange = (e) => {
    const category = e.target.value;
    setIndustryCategory(category);
    setIndustrySubcategory(''); // サブカテゴリをリセット
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});

    try {
      // バリデーション
      const errors = {};
      if (!fullName.trim()) errors.fullName = "氏名を入力してください";
      if (!universityId) {
        errors.universityId = "出身大学を選択してください";
      } else if (universityId === OTHER_UNIVERSITY_ID && !otherUniversityName.trim()) {
        errors.otherUniversityName = "出身大学名を入力してください";
      }
      if (!graduationYear) errors.graduationYear = "卒業年を選択してください";
      if (!industryCategory) errors.industryCategory = "業界カテゴリを選択してください";
      if (!industrySubcategory) errors.industrySubcategory = "具体的な業界を選択してください";
      if (!company.trim()) errors.company = "会社名を入力してください";
      if (!messageToStudents.trim()) errors.messageToStudents = "学生への一言メッセージを入力してください";

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        setSaving(false);
        return;
      }

      const university = universities.find((uni) => uni.id === universityId);
      const isOtherUniversity = universityId === OTHER_UNIVERSITY_ID;

      // 更新データの準備
      const updateData = {
        氏名: fullName.trim(),
        出身大学: isOtherUniversity
          ? otherUniversityName.trim()
          : (university?.name || ''),
        大学ID: universityId,
        isOfficialUniversity: !isOtherUniversity,
        卒業した年: parseInt(graduationYear),
        就職先業界: `${industryCategory} - ${industrySubcategory}`, // カテゴリとサブカテゴリを組み合わせて保存
        会社名: company.trim(),
        一言メッセージ: messageToStudents.trim()
      };

      // プロフィール画像が新しくアップロードされた場合
      if (profileImage) {
        updateData.profileImageFile = profileImage;
      }

      await updateMentor(currentUser.uid, updateData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/mentor-profile');
      }, 1500);
    } catch (err) {
      console.error('Error updating mentor profile:', err);
      setError('プロフィールの更新に失敗しました。もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/mentor-profile');
  };

  if (loading) return <div className="loading">読み込み中...</div>;
  if (!mentor) return <div className="error">OG情報が見つかりません。</div>;

  return (
    <Layout>
      <div className="profile-container">
        <h2 className="profile-heading">プロフィール編集</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">プロフィールを更新しました！</div>}
        
        <form onSubmit={handleSubmit} className="profile-edit-form">
          <div className="form-section">
            <h3>基本情報</h3>
            
            <div className="form-group">
              <label htmlFor="fullName">
                氏名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="fullName"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="山田 花子"
              />
              {fieldErrors.fullName && (
                <div className="error-message">{fieldErrors.fullName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="universityId">
                出身大学 <span className="required">*</span>
              </label>
              <select
                id="universityId"
                value={universityId}
                onChange={handleUniversityChange}
                required
                disabled={universitiesLoading}
              >
                <option value="">選択してください</option>
                {universities.map((uni) => (
                  <option key={uni.id} value={uni.id}>
                    {uni.name}
                  </option>
                ))}
                <option value={OTHER_UNIVERSITY_ID}>{OTHER_UNIVERSITY_LABEL}</option>
              </select>
              {universityId === OTHER_UNIVERSITY_ID && (
                <input
                  type="text"
                  id="otherUniversityName"
                  value={otherUniversityName}
                  onChange={(e) => setOtherUniversityName(e.target.value)}
                  placeholder="大学名を入力してください"
                  maxLength={OTHER_UNIVERSITY_NAME_MAX_LENGTH}
                  required
                  style={{ marginTop: '0.5rem' }}
                />
              )}
              {fieldErrors.universityId && (
                <div className="error-message">{fieldErrors.universityId}</div>
              )}
              {fieldErrors.otherUniversityName && (
                <div className="error-message">{fieldErrors.otherUniversityName}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="graduationYear">
                卒業年 <span className="required">*</span>
              </label>
              <select
                id="graduationYear"
                value={graduationYear}
                onChange={(e) => setGraduationYear(e.target.value)}
                required
              >
                <option value="">選択してください</option>
                {generateGraduationYears().map((year) => (
                  <option key={year} value={year}>
                    {year}年
                  </option>
                ))}
              </select>
              {fieldErrors.graduationYear && (
                <div className="error-message">{fieldErrors.graduationYear}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="profileImage">
                プロフィール画像
              </label>
              <input
                type="file"
                id="profileImage"
                accept="image/*"
                onChange={handleImageChange}
              />
              <p className="field-hint">
                JPEGまたはPNG形式、5MB以下の画像をアップロードしてください。
                画像は自動的にリサイズされます。変更しない場合は選択不要です。
              </p>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="プロフィール画像プレビュー" />
                </div>
              )}
              {fieldErrors.profileImage && (
                <div className="error-message">{fieldErrors.profileImage}</div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>就職先情報</h3>
            
            <div className="form-group">
              <label htmlFor="industryCategory">
                業界カテゴリ <span className="required">*</span>
              </label>
              <select
                id="industryCategory"
                value={industryCategory}
                onChange={handleIndustryCategoryChange}
                required
              >
                <option value="">選択してください</option>
                {Object.keys(industryCategories).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              {fieldErrors.industryCategory && (
                <div className="error-message">{fieldErrors.industryCategory}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="industrySubcategory">
                具体的な業界 <span className="required">*</span>
              </label>
              <select
                id="industrySubcategory"
                value={industrySubcategory}
                onChange={(e) => setIndustrySubcategory(e.target.value)}
                required
                disabled={!industryCategory}
              >
                <option value="">
                  {industryCategory ? "選択してください" : "まず業界カテゴリを選択してください"}
                </option>
                {industryCategory &&
                  industryCategories[industryCategory].map((subcategory) => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
              </select>
              {fieldErrors.industrySubcategory && (
                <div className="error-message">{fieldErrors.industrySubcategory}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="company">
                会社名 <span className="required">*</span>
              </label>
              <input
                type="text"
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                required
                placeholder="株式会社○○"
              />
              {fieldErrors.company && (
                <div className="error-message">{fieldErrors.company}</div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h3>学生の皆さんへ</h3>
            
            <div className="form-group">
              <label htmlFor="messageToStudents">
                一言メッセージ <span className="required">*</span>
              </label>
              <textarea
                id="messageToStudents"
                value={messageToStudents}
                onChange={(e) => setMessageToStudents(e.target.value)}
                required
                rows="5"
                placeholder="学生の皆さんへのメッセージを入力してください..."
              />
              {fieldErrors.messageToStudents && (
                <div className="error-message">{fieldErrors.messageToStudents}</div>
              )}
            </div>
          </div>

          <div className="form-buttons">
            <button 
              type="button" 
              onClick={handleCancel}
              className="cancel-button"
              disabled={saving}
            >
              キャンセル
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={saving}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default MentorProfileEdit;